import { getSupabaseClient } from '../../supabase/client'
import { APP_SETTINGS } from '../../config/appSettings'

// Replay keys and the bounded Context briefs actually sent to Jev are allowed.
// Raw note and query text never enter this dev-only log; summaries are private.
type Feature = 'jev_search' | 'context_suggest' | 'search_comparison'
type Outcome = 'started' | 'ok' | 'skipped' | 'failed'
interface Details {
  candidateCount?: number; contextCount?: number; resultCount?: number
  elapsedMs?: number; providerMs?: number; costMicrousd?: number
  contextIds?: readonly string[]; contextRevisions?: readonly number[]; requestHash?: string
  contextBriefs?: readonly { id: string; title: string; purpose: string; summary?: string }[]
  contextScores?: readonly { id: string; score: number; choiceConfidence?: number }[]
}
interface Row {
  event_id: string; owner_id: string; flow_id: string; occurred_at: string
  feature: Feature; stage: string; outcome: Outcome; reason: string | null
  note_id: string | null; note_revision_id: string | null
  context_ids: string[] | null; context_revisions: number[] | null; context_briefs: { id: string; title: string; purpose: string; summary?: string }[] | null; request_hash: string | null
  context_scores: { id: string; score: number; choiceConfidence?: number }[] | null
  candidate_count: number | null; context_count: number | null; result_count: number | null
  elapsed_ms: number | null; provider_ms: number | null; cost_microusd: number | null
}

const MAX_QUEUED = APP_SETTINGS.intelligenceApi.smartPilot.devLogMaxQueuedEvents
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const CONTEXT_ID = /^[A-Za-z0-9][A-Za-z0-9_.:-]{0,127}$/
const SHA256 = /^[a-f0-9]{64}$/
const key = (owner: string) => `minimany:dev-flow-events:v1:${owner}`
const number = (value: number | undefined, limit: number) =>
  typeof value === 'number' && Number.isFinite(value) ? Math.min(limit, Math.max(0, Math.round(value))) : null
const code = (value: string) => /^[a-z][a-z0-9_]{0,39}$/.test(value) ? value : 'unknown'
let flushing = false
const flushTimers = new Map<string, ReturnType<typeof setTimeout>>()

function queued(owner: string): Row[] {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(key(owner)) ?? '[]')
    return Array.isArray(value) ? value.filter(row => row && typeof row === 'object' && row.owner_id === owner).slice(-MAX_QUEUED) as Row[] : []
  } catch { return [] }
}

function save(owner: string, rows: Row[]) {
  try { localStorage.setItem(key(owner), JSON.stringify(rows.slice(-MAX_QUEUED))) } catch { /* Diagnostics never block product use. */ }
}

export async function flushDevFlowEvents(owner: string): Promise<void> {
  if (flushing || !navigator.onLine) return
  flushing = true
  let sent = false
  try {
    const rows = queued(owner)
    if (!rows.length) return
    const client = await getSupabaseClient()
    const { data } = await client.auth.getSession()
    if (data.session?.user.id !== owner) return
    const { error } = await client.from('dev_flow_events').upsert(rows, { onConflict: 'event_id', ignoreDuplicates: true })
    if (!error) {
      const sentIds = new Set(rows.map(row => row.event_id))
      save(owner, queued(owner).filter(row => !sentIds.has(row.event_id)))
      sent = true
    }
  } catch { /* Retain the bounded queue for the next online attempt. */ }
  finally {
    flushing = false
    if (sent && queued(owner).length) queueMicrotask(() => void flushDevFlowEvents(owner))
  }
}

function scheduleFlush(owner: string) {
  if (!navigator.onLine || flushTimers.has(owner)) return
  flushTimers.set(owner, setTimeout(() => {
    flushTimers.delete(owner)
    void flushDevFlowEvents(owner)
  }, APP_SETTINGS.intelligenceApi.smartPilot.devLogFlushDelayMs))
}

export interface DevFlow {
  id: string
  identifyNote: (noteId: string, noteRevisionId?: string) => void
  event: (stage: string, outcome: Outcome, reason?: string, details?: Details) => void
}

export function createDevFlow(owner: string, feature: Feature, note?: { noteId: string; noteRevisionId?: string }): DevFlow {
  const id = crypto.randomUUID()
  let noteId = note?.noteId && UUID.test(note.noteId) ? note.noteId : null
  let noteRevisionId = noteId && note?.noteRevisionId && UUID.test(note.noteRevisionId) ? note.noteRevisionId : null
  return { id, identifyNote(nextNoteId, nextRevisionId) {
    noteId = UUID.test(nextNoteId) ? nextNoteId : null
    noteRevisionId = noteId && nextRevisionId && UUID.test(nextRevisionId) ? nextRevisionId : null
  }, event(stage, outcome, reason, details = {}) {
    const contextIds = details.contextIds && details.contextIds.length <= 100
      && details.contextIds.every(value => CONTEXT_ID.test(value)) ? [...details.contextIds] : null
    const contextRevisions = contextIds && details.contextRevisions?.length === contextIds.length
      && details.contextRevisions.every(value => Number.isSafeInteger(value) && value >= 0)
      ? [...details.contextRevisions] : null
    const candidateBriefs = contextIds && details.contextBriefs?.length === contextIds.length
      && details.contextBriefs.every((brief, index) => brief && brief.id === contextIds[index]
        && typeof brief.title === 'string' && brief.title.length <= 160
        && typeof brief.purpose === 'string' && brief.purpose.length <= 500
        && (brief.summary === undefined || (typeof brief.summary === 'string' && brief.summary.length <= APP_SETTINGS.intelligenceApi.smartPilot.maxContextSummaryChars)))
      ? details.contextBriefs.map(brief => ({ id: brief.id, title: brief.title, purpose: brief.purpose,
        ...(brief.summary ? { summary: brief.summary } : {}) })) : null
    const contextBriefs = candidateBriefs && new TextEncoder().encode(JSON.stringify(candidateBriefs)).byteLength <= 65536
      ? candidateBriefs : null
    const contextScores = details.contextScores && details.contextScores.length <= 100
      && details.contextScores.every(item => CONTEXT_ID.test(item.id) && Number.isFinite(item.score) && item.score >= 0 && item.score <= 1
        && (item.choiceConfidence === undefined || (Number.isFinite(item.choiceConfidence) && item.choiceConfidence >= 0 && item.choiceConfidence <= 1)))
      ? details.contextScores.map(item => ({ id: item.id, score: item.score,
        ...(item.choiceConfidence === undefined ? {} : { choiceConfidence: item.choiceConfidence }) })) : null
    const row: Row = {
      event_id: crypto.randomUUID(), owner_id: owner, flow_id: id, occurred_at: new Date().toISOString(),
      feature, stage: code(stage), outcome, reason: reason ? code(reason) : null,
      note_id: noteId, note_revision_id: noteRevisionId,
      context_ids: contextIds, context_revisions: contextRevisions, context_briefs: contextBriefs,
      context_scores: contextScores,
      request_hash: details.requestHash && SHA256.test(details.requestHash) ? details.requestHash : null,
      candidate_count: number(details.candidateCount, 10000), context_count: number(details.contextCount, 10000),
      result_count: number(details.resultCount, 10000), elapsed_ms: number(details.elapsedMs, 3600000),
      provider_ms: number(details.providerMs, 3600000), cost_microusd: number(details.costMicrousd, 10000000),
    }
    save(owner, [...queued(owner), row])
    scheduleFlush(owner)
  } }
}

export function devFlowReason(error: unknown): string {
  if (error instanceof Error && 'code' in error && typeof error.code === 'string') return code(error.code)
  if (error instanceof DOMException && error.name === 'AbortError') return 'aborted'
  return 'unexpected_error'
}
